import { FinanceType, Origin } from '@upstart/harmony/lib/constants';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import React, { FunctionComponent } from 'react';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import SelectRow from './SelectRow';
import FinanceTypeRow from './FinanceTypeRow';
import MonthlyPaymentRow from './MonthlyPaymentRow';
import CashPaymentRow from './CashPaymentRow';
import './OfferCardHeader.scss';
import { NormalizedOffer } from '../DeskingOfferCard/types';

interface Action {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface Props {
  offer: DeskingOffer;
  localOfferState: NormalizedOffer;
  errors?: Record<string, string>;
  financeType?: FinanceType;
  messagingOrigin?: Origin | null;
  months?: number;
  monthlyPayment?: number[];
  onePayment?: number | null;
  mileage?: number;
  cashPayment?: number;
  actions?: Action[];
  onSelectedChange?: (input: boolean) => void;
  onChange?: (input: { months?: number; mileage?: number }) => void;
  displayingAllSelectedOffers?: boolean;
  showBiweeklyPayments?: boolean;
  onPaymentRollbackChange: (
    field: string,
    type?: 'price' | 'percentage' | 'number' | 'checkbox',
    maxValue?: number | null,
  ) => (value: number) => void;
  readOnlyMode: boolean;
  isReadOnlyLoading: boolean;
  isViewingCurrentPencilVersion: boolean;
  dealer: DealerWithFlags;
}

const OfferCardHeader: FunctionComponent<Props> = ({
  offer,
  showBiweeklyPayments,
  localOfferState,
  errors,
  financeType,
  messagingOrigin,
  months,
  monthlyPayment,
  onePayment,
  mileage,
  cashPayment,
  actions,
  onSelectedChange,
  onChange,
  displayingAllSelectedOffers,
  onPaymentRollbackChange,
  readOnlyMode,
  isReadOnlyLoading,
  isViewingCurrentPencilVersion,
  dealer,
}) => {
  let paymentRow;
  const isEmptyCarPrice = !offer.data?.price;
  const hasTradeInAllowance = !!offer.data?.tradeInAllowance;
  const showEmptyPayment = isEmptyCarPrice && hasTradeInAllowance;
  if (financeType === FinanceType.CASH) {
    paymentRow = (
      <CashPaymentRow cashPayment={showEmptyPayment ? 0 : cashPayment} />
    );
  } else if (financeType === FinanceType.LEASE && onePayment) {
    paymentRow = (
      <CashPaymentRow cashPayment={showEmptyPayment ? 0 : onePayment} />
    );
  } else {
    paymentRow = (
      <MonthlyPaymentRow
        deskingOffer={offer}
        monthlyPayment={showEmptyPayment ? [0] : monthlyPayment}
        showBiweeklyPayments={showBiweeklyPayments}
        financeType={financeType}
        months={months}
        onPaymentRollbackChange={onPaymentRollbackChange}
        readOnlyMode={readOnlyMode}
        dealer={dealer}
      />
    );
  }
  return (
    <div className="offer-card-header">
      {!readOnlyMode && (
        <SelectRow
          offerId={offer.id}
          sent={!!offer.sentToSalespersonAt}
          selected={!!localOfferState.isSelectedOffer}
          onSelectedChange={value => onSelectedChange?.(value)}
          actions={actions}
          messagingOrigin={messagingOrigin}
        />
      )}
      <FinanceTypeRow
        offer={offer}
        financeType={financeType}
        months={months}
        mileage={mileage}
        onChange={onChange}
        displayingAllSelectedOffers={displayingAllSelectedOffers}
        errors={errors}
        readOnlyMode={readOnlyMode}
        isReadOnlyLoading={isReadOnlyLoading}
        isViewingCurrentPencilVersion={isViewingCurrentPencilVersion}
        dealer={dealer}
      />
      {paymentRow}
    </div>
  );
};

export default OfferCardHeader;
